import { useCartUpsells } from './hooks/useCartUpsells'
import { StandardProductCard } from '@/features/product/components/StandardProductCard'
import classNames from 'classnames'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import useEmblaCarousel from 'embla-carousel-react'
import { Text, Carousel, ProductCard } from '@lounge-fe/ui-kit'
import { usePreventEdgeScrolling } from '@/hooks/usePreventEdgeScrolling'
import { ErrorBoundary } from '@sentry/react'
import { useAlgoliaDataExtension } from '@/features/product/hooks/useAlgoliaDataExtension/useAlgoliaDataExtension'
import { ProductHit } from '@/features/product/types'
import { useEffect } from 'react'
import { useStatsig } from '@/features/experimentation/hooks'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'

export const Upsells = () => {
  const { t } = useTranslation()
  const { getExperiment } = useStatsig()
  const { upsells, error, isLoading, setModel } = useCartUpsells()
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  })
  const upsellExperiment = getExperiment('cart_upsells_model', {
    disableExposureLog: true,
  })

  const recommendationModel = upsellExperiment?.get('model')

  useEffect(() => {
    // Log exposure for control group (no recommendation model) or when component is in view
    if (!recommendationModel || inView) {
      getExperiment('cart_upsells_model')
    }
  }, [recommendationModel, inView])

  if (!recommendationModel) return null

  useEffect(() => {
    if (recommendationModel) {
      setModel(recommendationModel)
    }
  }, [recommendationModel])

  const extendedProducts = useAlgoliaDataExtension(
    upsells as ProductHit[],
    true,
  )

  const [emblaRef, embla] = useEmblaCarousel(
    {
      align: 'start',
      containScroll: 'trimSnaps',
      skipSnaps: true,
      active: true,
      loop: false,
    },
    [WheelGesturesPlugin()],
  )

  embla?.on('scroll', usePreventEdgeScrolling(embla))

  useEffect(() => {
    if (embla?.scrollProgress() !== 0) {
      embla?.scrollTo(0)
    }
  }, [upsells])

  if (error || (!upsells.length && !isLoading)) return null

  return (
    <ErrorBoundary>
      <div ref={ref} className="basket-upsells mb-4">
        <div className="px-4 md:px-10 mb-4">
          <Text variant="title-five">{t('cart.you_may_also_like')}</Text>
        </div>

        <div className="overflow-hidden px-4 md:px-10">
          <Carousel emblaRef={emblaRef} embla={embla}>
            <Carousel.Viewport showOverflow>
              <Carousel.Container
                className={classNames('auto-cols-[calc(70%-12px)] gap-x-2')}
              >
                {isLoading
                  ? Array.from({ length: 4 }).map((_, idx) => (
                      <Carousel.Slide index={idx} key={idx}>
                        <ProductCard.Skeleton key={idx} />
                      </Carousel.Slide>
                    ))
                  : extendedProducts.map((hit, idx) => (
                      <Carousel.Slide index={idx} key={hit.objectID}>
                        <StandardProductCard
                          key={hit.objectID}
                          aspectRatio="2/3"
                          product={hit}
                          useQuickAdd
                          showExclusiveQuickAdd
                          showWishlist={false}
                          onSuccess={() => {}}
                        />
                      </Carousel.Slide>
                    ))}
              </Carousel.Container>
            </Carousel.Viewport>
          </Carousel>
        </div>
      </div>
    </ErrorBoundary>
  )
}
